// "Put it on my home screen." Android browsers offer a one-tap install we can trigger; iPhone
// doesn't, so the friend screen falls back to showing the two taps (Share, Add to Home Screen).

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();

addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as InstallEvent;
  for (const f of listeners) f();
});

/** The browser offered a one-tap install (Android Chrome). */
export const canInstall = (): boolean => !!deferred;
/** Called when the browser offers it later than the page drew. Returns an unsubscribe. */
export function onInstallable(f: () => void): () => void { listeners.add(f); return () => listeners.delete(f); }

/** The one-tap install, and whether they said yes. */
export async function installWithChoice(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const d = deferred;
  deferred = null;
  try {
    await d.prompt();
    const c = d.userChoice ? await d.userChoice : { outcome: 'accepted' as const };
    return c.outcome === 'accepted' ? 'accepted' : 'dismissed';
  } catch { return 'dismissed'; }
}

/** Shows the browser's own install prompt. False when this browser doesn't offer one. */
export async function installPrompt(): Promise<boolean> {
  if (!deferred) return false;
  const d = deferred;
  deferred = null;
  try { await d.prompt(); return true; } catch { return false; }
}

/** Opened from a home-screen icon, not a browser tab. */
export const standalone = (): boolean =>
  matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
